import { Component, EventEmitter, OnInit } from '@angular/core';
import { DwButtonConfig, DwModalConfig, DwModalHandler, DwModalResult } from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  PostLoginAcknowledgementStep, PostLoginExperience
} from '../../models/post-login-experience.model';
import { PostLoginRepositoryService } from '../../services/post-login-repository.service';

@Component({
  selector: 'app-post-login-acknowledgement-modal',
  templateUrl: './post-login-acknowledgement-modal.component.html',
  styleUrls: ['./post-login-acknowledgement-modal.component.scss']
})
export class PostLoginAcknowledgementModalComponent implements OnInit, DwModalHandler {
  modalConfig: DwModalConfig<PostLoginExperience>;
  closeModal = new EventEmitter<DwModalResult<any>>();
  experience: PostLoginExperience;
  steps: PostLoginAcknowledgementStep[] = [];
  currentStep: PostLoginAcknowledgementStep;
  stepIndex = 0;
  saving = false;
  errorMessage = '';
  actionLabel = 'Acknowledge and continue';

  constructor(private repository: PostLoginRepositoryService) { }

  ngOnInit(): void {
    this.experience = this.modalConfig.data;
    if (this.experience.Terms) {
      this.steps.push({ type: 'terms', title: 'Terms of Service', positionLabel: '' });
    }
    if (this.experience.Documents && this.experience.Documents.length) {
      this.steps.push({ type: 'documents', title: 'New Documents', positionLabel: '' });
    }
    if (this.experience.Assignments && this.experience.Assignments.length) {
      this.steps.push({ type: 'assignments', title: 'New Assignments', positionLabel: '' });
    }
    this.steps = this.steps.map((step, index) => ({ ...step, positionLabel: `${index + 1} of ${this.steps.length}` }));
    this.setStep(0);
  }

  continue(): void {
    if (this.saving || !this.currentStep) {
      return;
    }
    this.saving = true;
    this.errorMessage = '';
    this.saveCurrentStep()
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: () => {
          if (this.stepIndex === this.steps.length - 1) {
            this.finish();
            return;
          }
          this.setStep(this.stepIndex + 1);
        },
        error: () => this.errorMessage = 'We could not save your acknowledgement. Please try again.'
      });
  }

  acknowledgementModeChanged(event: Event): void {
    this.experience.PersistAcknowledgements = (event.target as HTMLInputElement).checked;
    this.setActionLabel();
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult> {
    return of(new DwModalResult());
  }

  private saveCurrentStep(): Observable<any> {
    if (this.experience.IsEmulationTest && !this.experience.PersistAcknowledgements) {
      return of(null);
    }

    switch (this.currentStep.type) {
      case 'terms': return this.repository.acknowledgeTerms();
      case 'documents': return this.repository.acknowledgeDocuments(this.experience);
      case 'assignments': return this.repository.acknowledgeAssignments(this.experience);
      default: return of(null);
    }
  }

  private setStep(index: number): void {
    this.stepIndex = index;
    this.currentStep = this.steps[index];
    this.setActionLabel();
  }

  private setActionLabel(): void {
    const isLastStep = this.stepIndex === this.steps.length - 1;
    const isPreview = this.experience.IsEmulationTest && !this.experience.PersistAcknowledgements;

    if (isPreview) {
      this.actionLabel = isLastStep ? 'Finish preview' : 'Continue preview';
      return;
    }

    this.actionLabel = isLastStep ? 'Acknowledge and finish' : 'Acknowledge and continue';
  }

  private finish(): void {
    const result = new DwModalResult();
    result.cancelled = false;
    result.closeModal = true;
    this.closeModal.emit(result);
  }
}
